//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import Vendors from './vendor.model';

/**
 * @service VendorsService
 * @description Service for managing Vendor master data within the Material Management System (MMS).
 * This service is responsible for retrieving vendor information, which is crucial for procurement processes,
 * tracking payables, and managing supplier relationships.
 *
 * It interacts with the `/mms/vendors/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Vendor Retrieval**: Fetching a master list of all vendors.
 * 2. **Filtered Retrieval**: Getting vendors associated with a specific business or those who supply a particular item.
 * 3. **Role-Based Security**: Sanitizing request payloads based on the user's role to ensure data isolation.
 * 4. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * **Data Flow:**
 * - **List**: Component calls `getAllVendors()` or `getVendorsByBusinessId()` -> Service calls API -> Returns list of `Vendors`.
 * - **Item-Specific List**: Component calls `getVendorsByBusinessIdItemId()` -> Service checks role, sanitizes payload -> Service posts to API -> Returns filtered list of `Vendors`.
 *
 * @feature 1. Retrieve all vendors.
 * @feature 2. Filter vendors by the items they supply.
 * @feature 3. Role-based access control for vendor data.
 *
 * @architecture
 * Extends base Service<Vendors> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /mms/vendors/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE', affecting payload sanitization.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class VendorsService extends Service<Vendors> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/mms/vendors/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/vendors/');
  }

  /**
   * @method getVendorsByBusinessId
   * @async
   * @description Fetch vendors associated with a specific business ID.
   *
   * Note: The current implementation fetches all vendors with a large page size, and the `businessId`
   * parameter is not used in the URL construction. This may be intended to fetch all vendors
   * within a context determined by the backend based on the user's token.
   *
   * @param {string} businessId - The unique identifier of the business (currently unused in URL).
   * @returns {Promise<Array<Vendors>>} Promise resolving to an array of Vendors objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * vendorsService.getVendorsByBusinessId('bus-123').then(vendors => {
   *   console.log('Vendors:', vendors);
   * });
   */
  public getVendorsByBusinessId(businessId: string) {
    return new Promise<Array<Vendors>>((resolve, reject) => {
      const request = this.httpClient.get<Array<Vendors>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getVendorsByBusinessIdItemId
   * @async
   * @description Fetch vendors who supply a specific item, filtered by business context.
   *
   * **Security Logic:**
   * - Checks `localStorage.loginPersonType`.
   * - If the user is **NOT** an 'EMPLOYEE', the `businessId` property is removed from the payload
   *   to prevent unauthorized queries across different businesses.
   *
   * @param {string} businessId - The unique identifier of the business.
   * @param {string} itemId - The unique identifier of the item.
   * @returns {Promise<Array<Vendors>>} Promise resolving to the filtered list of vendors.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Modifies the payload (removes `businessId`) if user is not an employee.
   * - Initiates an HTTP POST request to `businessId/itemId`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getVendorsByBusinessIdItemId(businessId: string, itemId: string) {
    let payload = { businessId, itemId}
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete payload.businessId;
    }
  return new Promise<Array<Vendors>>((resolve, reject) => {
      const request = this.httpClient.post<Array<Vendors>>(`${this.baseUrl}${this.serviceUrl}businessId/itemId`, payload, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAllVendors
   * @async
   * @description Fetch all vendor records from the backend.
   *
   * Retrieves a large dataset (pageSize=20000) of vendors, ordered by creation date ('new').
   * This is functionally similar to `getVendorsByBusinessId` in its current implementation.
   *
   * @returns {Promise<Array<Vendors>>} Promise resolving to an array of all Vendors objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * vendorsService.getAllVendors().then(vendors => {
   *   console.log('All Vendors:', vendors);
   * });
   */
  public getAllVendors() {
    return new Promise<Array<Vendors>>((resolve, reject) => {
      const request = this.httpClient.get<Array<Vendors>>(`${this.baseUrl}${this.serviceUrl}?pageSize=20000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
